import React, {FC, useState, useEffect} from 'react'
import {Modal, Button, Alert} from 'react-bootstrap'
import {discordApiService} from '../services/discordApi'

interface DiscordConnectModalProps {
  show: boolean
  onClose: () => void
  onSuccess: () => void
}

const DiscordConnectModal: FC<DiscordConnectModalProps> = ({
  show,
  onClose,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [authUrl, setAuthUrl] = useState<string>('')

  // Verifica status e gera URL de autorização quando o modal abre
  useEffect(() => {
    if (show) {
      checkStatusAndGenerateUrl()
    }
  }, [show])

  const checkStatusAndGenerateUrl = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      // Verifica o status da integração
      const status = await discordApiService.getStatus()
      
      if (status.is_connected) {
        // Se já está conectado, apenas fecha o modal
        onSuccess()
        return
      }

      // Gera URL de autorização diretamente (credenciais estão no .env)
      await generateAuthUrl()
    } catch (error: any) {
      console.error('Error checking status:', error)
      setError('Erro ao verificar status da integração')
      setIsLoading(false)
    }
  }

  const generateAuthUrl = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const returnUrl = `${window.location.origin}/admin/integrations`
      const url = await discordApiService.getAuthorizationUrl(returnUrl)
      
      setAuthUrl(url)
    } catch (error: any) {
      console.error('Error generating auth URL:', error)
      setError(error.response?.data?.message || 'Erro ao gerar URL de autorização. Verifique se as credenciais estão configuradas no servidor.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleConnect = () => {
    if (authUrl) {
      // Abre a URL de autorização em nova janela
      const newWindow = window.open(
        authUrl,
        'Discord OAuth',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      )

      if (!newWindow) {
        setError('Não foi possível abrir a janela de autorização. Verifique se os pop-ups estão bloqueados.')
        return
      }

      // Monitora quando a janela é fechada
      const checkClosed = setInterval(() => {
        if (newWindow.closed) {
          clearInterval(checkClosed)
          // Verifica o status após fechar a janela
          setTimeout(async () => {
            try {
              const status = await discordApiService.getStatus()
              if (status.is_connected) {
                onSuccess()
              } else {
                // Verifica se há erro na URL (callback do OAuth)
                const urlParams = new URLSearchParams(window.location.search)
                const errorParam = urlParams.get('error')
                const messageParam = urlParams.get('message')
                
                if (errorParam === 'integration_exists' || messageParam?.includes('integração Discord ativa')) {
                  setError(messageParam || 'Já existe uma integração Discord ativa para esta instância. Apenas uma integração é permitida por instância.')
                }
              }
            } catch (error) {
              console.error('Error checking status after OAuth:', error)
              // Verifica se há erro na URL (callback do OAuth)
              const urlParams = new URLSearchParams(window.location.search)
              const errorParam = urlParams.get('error')
              const messageParam = urlParams.get('message')
              
              if (errorParam === 'integration_exists' || messageParam?.includes('integração Discord ativa')) {
                setError(messageParam || 'Já existe uma integração Discord ativa para esta instância. Apenas uma integração é permitida por instância.')
              }
            }
          }, 1000)
        }
      }, 500)
    }
  }

  const handleClose = () => {
    setError(null)
    setAuthUrl('')
    onClose()
  }

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className='fab fa-discord text-primary me-2'></i>
          Conectar Discord ao TaskRush
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div>
          <div className='mb-4'>
            <p className='text-body' style={{opacity: 0.85, fontSize: '1rem'}}>
              O TaskRush usa um APP único do Discord. Você só precisa autorizar o bot no seu servidor Discord.
            </p>
            <p className='text-body mt-2' style={{opacity: 0.75, fontSize: '0.95rem'}}>
              Clique no botão abaixo para autorizar o TaskRush a acessar seu servidor Discord e começar a criar tarefas diretamente do Discord.
            </p>
          </div>

          {error && (
            <Alert variant='danger' className='mb-4'>
              <i className='fas fa-exclamation-triangle me-2'></i>
              {error}
            </Alert>
          )}

          {isLoading ? (
            <div className='text-center py-4'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Carregando...</span>
              </div>
              <p className='text-body mt-3' style={{opacity: 0.85}}>Preparando autorização...</p>
            </div>
          ) : authUrl ? (
            <div className='d-grid gap-2'>
              <Button 
                variant="primary" 
                size="lg"
                onClick={handleConnect}
              >
                <i className='fab fa-discord me-2'></i>
                Autorizar no Discord
              </Button>
              
              <Button 
                variant="outline-secondary" 
                onClick={handleClose}
              >
                Cancelar
              </Button>
            </div>
          ) : (
            <div className='text-center py-4'>
              <p className='text-body' style={{opacity: 0.85}}>Erro ao gerar URL de autorização</p>
              <div className='d-grid gap-2'>
                <Button 
                  variant="outline-primary" 
                  onClick={generateAuthUrl}
                >
                  Tentar novamente
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleClose}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default DiscordConnectModal

